import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, Alert } from 'react-bootstrap';
import { Plus, Search, Edit, Trash2, Eye, MapPin, Users, ArrowLeft, Save, X, Building2 } from 'lucide-react';
import { 
    getAllZones, 
    getZoneById, 
    createZone, 
    updateZone, 
    deleteZone,
    getRetailersByZone 
} from '../api/zones';
import { getUsersByRole } from '../api/users';
import useAuth from '../hooks/useAuth';
import useForm from '../hooks/useForm';
import { getAllMagasins } from '../api/magasins';
import { useLocation } from 'react-router-dom';

const ZoneManagement = () => {
    const { isAdmin, isManager, isCommercial } = useAuth();
    
    // États principaux
    const [view, setView] = useState('list'); // 'list', 'detail', 'form'
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);
    const [retailers, setRetailers] = useState([]);
    const [commercials, setCommercials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [magasins, setMagasins] = useState([]);
    
    // États pour les filtres et recherche
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [filteredZones, setFilteredZones] = useState([]);
    
    // Formulaire zone avec useForm hook
    const initialValues = {
        name: '',
        code: '',
        description: '',
        commercialId: '',
        active: true
    };
    
    const { values, handleChange, setValues, errors, validateForm, resetForm } = useForm(initialValues);

    useEffect(() => {
        fetchZones();
        fetchCommercials();
    }, []);

    useEffect(() => {
        filterZones();
    }, [searchTerm, filterActive, zones]);

    const fetchZones = async () => {
        try {
            setLoading(true);
            const data = await getAllZones();
            setZones(Array.isArray(data) ? data : []);
            setError('');
        } catch (err) {
            setError('Erreur lors du chargement des zones');
            setZones([]);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMagasinsByZone = async (zoneId) => {
    try {
        // Récupérer tous les magasins et filtrer par zone
        const allMagasins = await getAllMagasins();
        const zoneMagasins = allMagasins.filter(m => m.zoneId === zoneId);
        setMagasins(Array.isArray(zoneMagasins) ? zoneMagasins : []);
    } catch (err) {
        console.error('Erreur lors du chargement des magasins:', err);
        setMagasins([]);
    }
};

    const fetchCommercials = async () => {
    try {
        const users = await getUsersByRole('SALES_REP');  // ← Changer ici
        setCommercials(Array.isArray(users) ? users : []);
    } catch (err) {
        console.error('Erreur lors du chargement des commerciaux:', err);
        setCommercials([]);
    }
};

    const fetchZoneDetails = async (id) => {
    try {
        setLoading(true);
        const data = await getZoneById(id);
        setSelectedZone(data);
        const retailersData = await getRetailersByZone(id);
        setRetailers(Array.isArray(retailersData) ? retailersData : []);
        await fetchMagasinsByZone(id);  // ← AJOUTER cette ligne
    } catch (err) {
        setError('Erreur lors du chargement des détails');
        setRetailers([]);
        setMagasins([]);  // ← AJOUTER
        console.error(err);
    } finally {
        setLoading(false);
    }
};

    const filterZones = () => {
        let filtered = zones;

        if (filterActive === 'active') {
            filtered = filtered.filter(z => z.active);
        } else if (filterActive === 'inactive') {
            filtered = filtered.filter(z => !z.active);
        }

        if (searchTerm) {
            filtered = filtered.filter(z =>
                z.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                z.code.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredZones(filtered);
    };

    const handleViewDetails = (zone) => {
        setSelectedZone(zone);
        fetchZoneDetails(zone.id);
        setView('detail');
    };

    const handleCreateNew = () => {
        resetForm();
        setSelectedZone(null);
        setView('form');
    };

    const handleEdit = (zone) => {
        setSelectedZone(zone);
        setValues({
            name: zone.name || '',
            code: zone.code || '',
            description: zone.description || '',
            commercialId: zone.commercialId || '',
            active: zone.active ?? true
        });
        setView('form');
    };

    const handleDelete = async (id, name) => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer la zone "${name}" ?`)) {
            try {
                await deleteZone(id);
                setSuccess('Zone supprimée avec succès');
                fetchZones();
            } catch (err) {
                setError(err.response?.data?.message || 'Erreur lors de la suppression');
            }
        }
    };

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        
        const validationSchema = {
            name: { required: true, errorMessage: 'Le nom est requis' },
            code: { required: true, errorMessage: 'Le code est requis' }
        };

        if (!validateForm(validationSchema)) {
            return;
        }

        try {
            setLoading(true);
            const zoneData = {
                ...values,
                commercialId: values.commercialId ? Number(values.commercialId) : null
            };

            if (selectedZone) {
                await updateZone(selectedZone.id, zoneData);
                setSuccess('Zone mise à jour avec succès');
            } else {
                await createZone(zoneData);
                setSuccess('Zone créée avec succès');
            }

            fetchZones();
            setTimeout(() => {
                setView('list');
                resetForm();
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToList = () => {
        setView('list');
        setSelectedZone(null);
        resetForm();
    };

    const location = useLocation();
  useEffect(() => {
    handleBackToList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

    // ========== RENDER VIEWS ==========

    // Boutons d'action réutilisés (tableau + cartes)
const renderZoneActions = (zone) => (
    <div className="d-flex gap-2 justify-content-end">
        <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(zone)} title="Voir les détails">
            <Eye size={16} />
        </Button>
        {(isAdmin || isManager) && (
            <>
                <Button variant="outline-warning" size="sm" onClick={() => handleEdit(zone)} title="Modifier">
                    <Edit size={16} />
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(zone.id, zone.name)} title="Supprimer">
                    <Trash2 size={16} />
                </Button>
            </>
        )}
    </div>
);

// Vue Liste
const renderListView = () => (
    <>
        <Row className="mb-4">
            <Col>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h2 className="mb-1">Gestion des Zones</h2>
                        <p className="text-muted mb-0">Liste de toutes les zones ({filteredZones.length})</p>
                    </div>
                    {(isAdmin || isManager) && (
                        <Button variant="primary" onClick={handleCreateNew}>
                            <Plus size={18} className="me-2" />
                            Nouvelle Zone
                        </Button>
                    )}
                </div>
            </Col>
        </Row>

        <Row className="mb-3 g-2">
            <Col xs={12} md={6}>
                <InputGroup>
                    <InputGroup.Text><Search size={18} /></InputGroup.Text>
                    <Form.Control
                        type="text"
                        placeholder="Rechercher par nom ou code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>
            </Col>
            <Col xs={12} md={3}>
                <Form.Select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
                    <option value="all">Toutes les zones</option>
                    <option value="active">Actives uniquement</option>
                    <option value="inactive">Inactives uniquement</option>
                </Form.Select>
            </Col>
        </Row>

        {filteredZones.length === 0 ? (
            <Card className="shadow-sm">
                <Card.Body>
                    <div className="text-center py-5">
                        <MapPin size={48} className="text-muted mb-3" />
                        <p className="text-muted mb-0">Aucune zone trouvée</p>
                    </div>
                </Card.Body>
            </Card>
        ) : (
            <>
                {/* ≥ lg : tableau */}
                <Card className="shadow-sm d-none d-lg-block">
                    <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th>Code</th>
                                    <th>Nom</th>
                                    <th>Commercial</th>
                                    <th>Revendeurs</th>
                                    <th>Statut</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredZones.map((zone) => (
                                    <tr key={zone.id}>
                                        <td><strong>{zone.code}</strong></td>
                                        <td>{zone.name}</td>
                                        <td>
                                            {zone.commercialName ? (
                                                <div className="d-flex align-items-center">
                                                    <Users size={14} className="me-1" />
                                                    {zone.commercialName}
                                                </div>
                                            ) : (
                                                <span className="text-muted">Non assigné</span>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg="info">{zone.retailerCount || 0} revendeur(s)</Badge>
                                        </td>
                                        <td>
                                            <Badge bg={zone.active ? 'success' : 'secondary'}>
                                                {zone.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>{renderZoneActions(zone)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* < lg : cartes */}
                <div className="d-lg-none">
                    {filteredZones.map((zone) => (
                        <Card key={zone.id} className="mb-2 shadow-sm">
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <div className="fw-bold">{zone.name}</div>
                                        <small className="text-muted">{zone.code}</small>
                                    </div>
                                    <Badge bg={zone.active ? 'success' : 'secondary'}>
                                        {zone.active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>

                                <div className="small mb-1">
                                    <span className="text-muted">Commercial : </span>
                                    {zone.commercialName || <span className="text-muted">Non assigné</span>}
                                </div>

                                <div className="small mb-3">
                                    <span className="text-muted">Revendeurs : </span>
                                    <Badge bg="info">{zone.retailerCount || 0}</Badge>
                                </div>

                                {renderZoneActions(zone)}
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            </>
        )}
    </>
);

    // Vue Formulaire
    const renderFormView = () => (
        <>
            <Row className="mb-4">
                <Col>
                    <div className="d-flex align-items-center gap-3">
                        <Button variant="outline-secondary" onClick={handleBackToList}>
                            <ArrowLeft size={18} />
                        </Button>
                        <h2 className="mb-0">{selectedZone ? 'Modifier la Zone' : 'Nouvelle Zone'}</h2>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col lg={8} className="mx-auto">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Form onSubmit={handleSubmitForm}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom de la Zone *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={values.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                placeholder="Ex: Zone Nord Dakar"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="code"
                                                value={values.code}
                                                onChange={handleChange}
                                                isInvalid={!!errors.code}
                                                placeholder="Ex: ZONE-DKR-N"
                                                disabled={selectedZone}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.code}</Form.Control.Feedback>
                                            {selectedZone && (
                                                <Form.Text className="text-muted">
                                                    Le code ne peut pas être modifié
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        placeholder="Description de la zone..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Commercial</Form.Label>
                                    <Form.Select name="commercialId" value={values.commercialId} onChange={handleChange}>
                                        <option value="">Sélectionner un commercial</option>
                                        {commercials.map((commercial) => (
                                            <option key={commercial.id} value={commercial.id}>
                                                {commercial.fullName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Check
                                        type="checkbox"
                                        name="active"
                                        label="Zone active"
                                        checked={values.active}
                                        onChange={(e) => handleChange({ target: { name: 'active', value: e.target.checked } })}
                                    />
                                </Form.Group>

                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="secondary" onClick={handleBackToList} disabled={loading}>
                                        <X size={18} className="me-2" />
                                        Annuler
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={loading}>
                                        <Save size={18} className="me-2" />
                                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );

    // Vue Détails
    const renderDetailView = () => {
        if (!selectedZone) return null;

        return (
            <>
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-3">
                                <Button variant="outline-secondary" onClick={handleBackToList}>
                                    <ArrowLeft size={18} />
                                </Button>
                                <div>
                                    <h2 className="mb-1">{selectedZone.name}</h2>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge bg={selectedZone.active ? 'success' : 'secondary'}>
                                            {selectedZone.active ? 'Active' : 'Inactive'}
                                        </Badge>
                                        <span className="text-muted">Code: {selectedZone.code}</span>
                                    </div>
                                </div>
                            </div>
                            {(isAdmin || isManager) && (
                                <Button variant="warning" onClick={() => handleEdit(selectedZone)}>
                                    <Edit size={18} className="me-2" />
                                    Modifier
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row className="mb-4">
    <Col md={4}>
        <Card className="shadow-sm">
            <Card.Body>
                <div className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                        <Users size={24} className="text-primary" />
                    </div>
                    <div>
                        <h6 className="text-muted mb-1">Revendeurs</h6>
                        <h3 className="mb-0">{retailers.length}</h3>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
    <Col md={4}>
        <Card className="shadow-sm">
            <Card.Body>
                <div className="d-flex align-items-center">
                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                        <Building2 size={24} className="text-success" />
                    </div>
                    <div>
                        <h6 className="text-muted mb-1">Magasins</h6>
                        <h3 className="mb-0">{magasins.length}</h3>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
    <Col md={4}>
        <Card className="shadow-sm">
            <Card.Body>
                <div className="d-flex align-items-center">
                    <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                        <MapPin size={24} className="text-info" />
                    </div>
                    <div>
                        <h6 className="text-muted mb-1">Commercial</h6>
                        <h5 className="mb-0">
                            {selectedZone.commercialName || (
                                <span className="text-muted">Non assigné</span>
                            )}
                        </h5>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
</Row>

                <Card className="shadow-sm">
                    <Card.Body>
                        <h5 className="mb-3">Informations de la Zone</h5>
                        <Row>
                            <Col md={6}>
                                <Table borderless>
                                    <tbody>
                                        <tr>
                                            <td className="text-muted" width="40%">Code</td>
                                            <td><strong>{selectedZone.code}</strong></td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Nom</td>
                                            <td><strong>{selectedZone.name}</strong></td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Description</td>
                                            <td><strong>{selectedZone.description || 'Aucune description'}</strong></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                            <Col md={6}>
                                <Table borderless>
                                    <tbody>
                                        <tr>
                                            <td className="text-muted" width="40%">Commercial</td>
                                            <td>
                                                <strong>
                                                    {selectedZone.commercialName || <span className="text-muted">Non assigné</span>}
                                                </strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Date de création</td>
                                            <td><strong>{new Date(selectedZone.createdAt).toLocaleDateString('fr-FR')}</strong></td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Dernière modification</td>
                                            <td><strong>{new Date(selectedZone.updatedAt).toLocaleDateString('fr-FR')}</strong></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>

                        <hr />

                        <h5 className="mb-3">Revendeurs de la Zone ({retailers.length})</h5>
                        {retailers.length === 0 ? (
                            <div className="text-center py-4">
                                <Users size={48} className="text-muted mb-3" />
                                <p className="text-muted">Aucun revendeur dans cette zone</p>
                            </div>
                        ) : (
                            <Table responsive hover>
                                <thead className="bg-light">
                                    <tr>
                                        <th>Nom</th>
                                        <th>Email</th>
                                        <th>Téléphone</th>
                                        <th>Ville</th>
                                        <th>Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {retailers.map((retailer) => (
                                        <tr key={retailer.id}>
                                            <td><strong>{retailer.name}</strong></td>
                                            <td>{retailer.email}</td>
                                            <td>{retailer.phone || '-'}</td>
                                            <td>{retailer.city || '-'}</td>
                                            <td>
                                                <Badge bg={retailer.active ? 'success' : 'secondary'}>
                                                    {retailer.active ? 'Actif' : 'Inactif'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </>
        );
    };

    // ========== RENDER PRINCIPAL ==========

    if (loading && view === 'list') {
        return (
            <Container fluid className="py-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {view === 'list' && renderListView()}
            {view === 'form' && renderFormView()}
            {view === 'detail' && renderDetailView()}
        </Container>
    );
};

export default ZoneManagement;